<template>
  <form
    class="application-form"
    @submit.prevent="onSubmit"
    data-cy="application-form"
  >
    <header
      class="ingredients-notification-header"
      data-cy="ingredients-notification-header"
    >
      All ingredients must appear on your Schedule A as approved or be submitted
      for approval.
    </header>
    <div class="form-control">
      <label class="form-label" for="product-name" data-cy="product-name-label"
        >Product Name</label
      >
      <InputField
        class="form-input-field"
        type="text"
        inputId="product-name"
        v-model="formData.productName"
        :maxlength="225"
        data-cy="product-name-input"
      />
      <font-awesome-icon
        class="question-icon"
        icon="fa-solid fa-circle-question"
        @click="helpBlocksVisibility.showProductLabel = true"
        data-cy="product-name-tooltip"
      />
    </div>
    <HelpBlock
      v-if="helpBlocksVisibility.showProductLabel"
      containerClass="help-block"
      @close="helpBlocksVisibility.showProductLabel = false"
      data-cy="product-name-help-block"
    >
      <div class="row form-row">
        <div class="col-sm-3">
          <strong>Item</strong>
        </div>
        <div class="col-sm-8">
          <strong>Description</strong>
        </div>
      </div>
      <div class="row form-row">
        <div class="col-sm-3">Product Name / Number</div>
        <div class="col-sm-8">
          Enter the name and/or number identifying the product. If there is no
          label name provided, the product name/number will appear on the Kosher
          Certificate (LOC).
        </div>
      </div>
      <div class="row form-row">
        <div class="col-sm-3">Label Name / Number</div>
        <div class="col-sm-8">
          Enter a label name and/or number
          <span style="color: red">ONLY</span> if it is different than the
          product name. If provided, the label information will appear on the
          Kosher Certificate (LOC) instead of the product name.
        </div>
      </div>
      <hr />
      <div>
        <b>Requirement:</b>
        <br />
        You must enter 1 or more of the following 4 fields: Product Name,
        Product Number, Label Name, Label Number. All 4 fields are not required.
      </div>
    </HelpBlock>
    <div class="form-control">
      <label
        class="form-label"
        for="product-description"
        data-cy="product-number-label"
        >Product Number</label
      >
      <InputField
        class="form-input-field"
        type="text"
        inputId="product-description"
        v-model="formData.productNumber"
        :maxlength="25"
        data-cy="product-number-input"
      />
    </div>
    <a
      class="label-link"
      @click="formData.showLabel = !formData.showLabel"
      data-cy="show-label-link"
    >
      <font-awesome-icon class="question-icon" icon="fa-solid fa-warning" />
      Label name/number different than product name/number? Click here.
    </a>
    <div v-show="formData.showLabel" class="form-labels">
      <div class="form-control">
        <label class="form-label" for="label-name" data-cy="label-name-label"
          >Label Name</label
        >
        <InputField
          class="form-input-field"
          type="text"
          inputId="label-name"
          v-model="formData.labelName"
          :maxlength="225"
          data-cy="label-name-input"
        />
      </div>
      <div class="form-control">
        <label
          class="form-label"
          for="label-description"
          data-cy="label-number-label"
          >Label Number</label
        >
        <InputField
          class="form-input-field"
          type="text"
          inputId="label-description"
          v-model="formData.labelNumber"
          :maxlength="25"
          data-cy="label-number-input"
        />
      </div>
    </div>
    <div
      v-if="showProductLabelError"
      ref="productLabelErrorRef"
      class="application-form__validation-error"
      data-cy="product-label-error"
    >
      <p>Please enter Name or Number for either Product or Label</p>
    </div>

    <hr />
    <div class="form-control">
      <label class="form-label" for="category-dropdown" data-cy="category-label"
        >Category</label
      >
      <DropDownList
        id="category-dropdown"
        :data-items="categories"
        text-field="Item1"
        data-item-key="Item2"
        :popup-settings="{ className: 'modal-popup' }"
        v-model="formData.selectedCategory"
        required
        data-cy="category-dropdown"
      />
    </div>
    <div class="form-control">
      <label class="form-label" for="ci-dropdown" data-cy="ci-label"
        >Consumer/Industrial</label
      >
      <DropDownList
        id="ci-dropdown"
        :data-items="ciOptions"
        text-field="text"
        data-item-key="value"
        :popup-settings="{ className: 'modal-popup' }"
        v-model="formData.selectedCI"
        required
        data-cy="ci-dropdown"
      />
    </div>
    <div
      v-if="formData.selectedCI && formData.selectedCI.value !== 'C'"
      class="form-control"
    >
      <label class="form-label" for="shipped-checkbox" data-cy="shipped-label"
        >Is the product shipped in tankers or trailers?</label
      >
      <Checkbox
        id="shipped-checkbox"
        v-model="formData.shippedTankersTrailers"
        data-cy="shipped-checkbox"
      />
    </div>
    <div class="form-control">
      <label
        class="form-label"
        for="confidential-dropdown"
        data-cy="confidential-label"
        >Confidentiality</label
      >
      <DropDownList
        id="confidential-dropdown"
        :data-items="confidentialOptions"
        text-field="text"
        data-item-key="value"
        :popup-settings="{ className: 'modal-popup' }"
        v-model="formData.selectedConfidential"
        :disabled="!formData.canSelectConfidential"
        required
        data-cy="confidential-dropdown"
      />
      <font-awesome-icon
        class="question-icon"
        icon="fa-solid fa-circle-question"
        @click="helpBlocksVisibility.showConfidentiality = true"
        data-cy="confidential-tooltip"
      />
    </div>
    <HelpBlock
      v-if="helpBlocksVisibility.showConfidentiality"
      containerClass="help-block"
      @close="helpBlocksVisibility.showConfidentiality = false"
      data-cy="confidential-help-block"
    >
      <div class="row form-row">
        <div class="col-sm-3">
          <b>Confidential:</b>
        </div>
        <div class="col-sm-8">
          This product will NOT appear on any OU websites
        </div>
      </div>
      <div class="row form-row">
        <div class="col-sm-3">
          <b>Not Confidential:</b>
        </div>
        <div class="col-sm-8">This product will appear on all OU Websites</div>
      </div>
      <div class="row form-row">
        <div class="col-sm-3">
          <b>List only on OU Universal Kosher Database (UKD):</b>
        </div>
        <div class="col-sm-8">
          This product will appear on the Universal Kosher Database available to
          OU Certified Companies only
        </div>
      </div>
    </HelpBlock>
    <div class="form-control">
      <label class="form-label" for="dpm-dropdown" data-cy="dpm-label"
        >D/P/M</label
      >
      <DropDownList
        id="dpm-dropdown"
        :data-items="dpmOptions"
        text-field="text"
        data-item-key="value"
        :popup-settings="{ className: 'modal-popup' }"
        v-model="formData.selectedDpm"
        required
        data-cy="dpm-dropdown"
      />
    </div>
    <div class="form-control">
      <Checkbox
        id="intermediate-mix-checkbox"
        v-model="formData.intermediateMix"
        label="Intermediate Mix (If checked, product will NOT appear on LOC)"
        data-cy="intermediate-mix-checkbox"
      />
    </div>
    <div class="form-control">
      <Checkbox
        id="passover-checkbox"
        v-model="formData.passover"
        label="Passover"
        data-cy="passover-checkbox"
      />
    </div>
    <header class="form-header" data-cy="brand-header">Brand</header>
    <div class="form-control">
      <DropDownList
        id="brand-dropdown"
        :data-items="brands"
        text-field="BrandSlashDistributorCompanyNamePLCompaniesOnly"
        data-item-key="Brand"
        :popup-settings="{ className: 'modal-popup' }"
        v-model="formData.selectedBrand"
        required
        data-cy="brand-dropdown"
      />
      <span
        class="clear-brand"
        v-if="!newBrandSelected"
        @click="clearBrandSelection"
      >
        <font-awesome-icon class="times-icon" icon="fa-solid fa-times" />
      </span>
    </div>
    <div v-if="newBrandSelected" class="or-divider">OR</div>
    <div v-if="newBrandSelected" class="form-control">
      <InputField
        class="form-input-field"
        type="text"
        inputId="brand-name"
        v-model="formData.newBrandName"
        placeholder="Add New Brand"
        :maxlength="75"
        required
        data-cy="brand-name-input"
      />
    </div>
    <div class="form-radio-control">
      <RadioButton
        name="brandGroup"
        value="In-House"
        :checked="selectedBrandType === 'In-House'"
        @change="handleBrandTypeChange"
        label="In-House"
        :disabled="!newBrandSelected"
        data-cy="in-house-radio"
      />
      <RadioButton
        name="brandGroup"
        value="Private Label"
        :checked="selectedBrandType === 'Private Label'"
        @change="handleBrandTypeChange"
        label="Private Label"
        :disabled="!newBrandSelected"
        data-cy="private-label-radio"
      />
    </div>
    <div
      v-if="newBrandSelected && selectedBrandType === 'Private Label'"
      class="new-brand-container"
      data-cy="new-brand-container"
    >
      <div class="form-control">
        <Checkbox
          v-model="formData.newBrandUniqueFormula"
          label="IS THE FORMULA FOR THIS PRODUCT UNIQUE TO THIS PRIVATE LABEL COMPANY?"
          data-cy="unique-formula-checkbox"
        />
      </div>
      <strong class="form-control-title" data-cy="distributor-info-title"
        >Please enter distributor info:</strong
      >
      <div class="form-control">
        <label
          class="form-label"
          for="company-name"
          data-cy="company-name-label"
          >Company Name</label
        >
        <input
          class="form-input"
          type="text"
          id="company-name"
          v-model="formData.newBrandCompanyName"
          data-cy="company-name-input"
        />
      </div>
      <div class="form-control">
        <label class="form-label" for="address" data-cy="address-label"
          >Address</label
        >
        <input
          class="form-input"
          type="text"
          id="address"
          v-model="formData.newBrandAddress"
          data-cy="address-input"
        />
      </div>
      <strong class="form-control-title" data-cy="contact-person-title"
        >Contact person:</strong
      >
      <div class="form-control">
        <label
          class="form-label"
          for="contact-name"
          data-cy="contact-name-label"
          >Name</label
        >
        <input
          class="form-input"
          type="text"
          id="contact-name"
          v-model="formData.newBrandContactName"
          data-cy="contact-name-input"
        />
      </div>
      <div class="form-control">
        <label
          class="form-label"
          for="contact-email"
          data-cy="contact-email-label"
          >Email</label
        >
        <input
          class="form-input"
          type="text"
          id="contact-email"
          v-model="formData.newBrandContactEmail"
          data-cy="contact-email-input"
        />
      </div>
      <div class="form-control">
        <label
          class="form-label"
          for="contact-phone"
          data-cy="contact-phone-label"
          >Phone</label
        >
        <input
          class="form-input"
          type="text"
          id="contact-phone"
          v-model="formData.newBrandContactPhone"
          data-cy="contact-phone-input"
        />
      </div>
    </div>
    <div class="documentation-container">
      <header class="form-header help">
        <span data-cy="documentation-header-title">Documentation</span>
        <div class="header-help-container pull-right">
          <a
            class="help-link"
            @click="helpBlocksVisibility.showNewLabel = true"
            data-cy="documentation-new-label-link"
            >New Label Information Requirements
            <font-awesome-icon
              class="question-icon"
              icon="fa-solid fa-circle-question"
            />
          </a>
          <a
            class="help-link"
            @click="helpBlocksVisibility.showNewIngredients = true"
            data-cy="documentation-new-ingredients-link"
            >Are there new ingredients in this product
            <font-awesome-icon
              class="question-icon"
              icon="fa-solid fa-circle-question"
            />
          </a>
        </div>
      </header>
      <HelpBlock
        v-if="helpBlocksVisibility.showNewLabel"
        headerText="Documentation Requirements"
        containerClass="help-block"
        @close="helpBlocksVisibility.showNewLabel = false"
        data-cy="documentation-new-label-help-block"
      >
        <p>
          Upload copy of product label or other documentation.
          <br />
          <br />
          For New Private Label Products, an image of the label is required.<br />
          (The OU will generate an agreement to be co-signed by manufacturer and
          distributor. Products will be certfied when co-signed&nbsp;agreement
          is received).
        </p>
      </HelpBlock>
      <HelpBlock
        v-if="helpBlocksVisibility.showNewIngredients"
        headerText="New Ingredients?"
        containerClass="help-block"
        @close="helpBlocksVisibility.showNewIngredients = false"
        data-cy="documentation-new-ingredients-help-block"
      >
        <p>
          When new product contains raw materials which do not exist on Schedule
          A (ingredient list), list ingredient information in comment box or
          upload supporting documentation.
          <br />
          <br />
          <strong
            >Submit the new ingredient request via Ingredients menu
            item.</strong
          >
        </p>
      </HelpBlock>
      <ProductAttachmentsView v-if="product?.Id" :productCartId="product.Id" />
      <strong class="form-control-title" data-cy="upload-label"
        >Upload Supporting Documentation</strong
      >
      <FileUpload v-model="formData.uploadedDocs" />
      <a
        class="label-link"
        @click="formData.showUpcCode = !formData.showUpcCode"
        data-cy="show-label-link"
      >
        <font-awesome-icon class="question-icon" icon="fa-solid fa-warning" />
        Have UPC code/number? Click here.
      </a>
      <div v-show="formData.showUpcCode" class="form-labels">
        <div class="form-control">
          <label class="form-label" for="upc-code" data-cy="upc-code-label"
            >UPC Code</label
          >
          <input
            class="form-input"
            type="text"
            id="upc-code"
            v-model="formData.upcCode"
            data-cy="upc-code-input"
          />
        </div>
      </div>
      <strong class="form-control-title" data-cy="comment-label"
        >Comment</strong
      >
      <textarea
        class="form-text-area"
        v-model="formData.docsComment"
        rows="5"
        data-cy="comment-input"
      ></textarea>
    </div>
    <header class="form-header" data-cy="plants-label">Plants</header>
    <p
      v-show="!anyPlantSelected"
      class="form-plant-error"
      data-cy="plant-error"
    >
      Please select at least one plant.
    </p>
    <DownloadsPlantGrid :plants="certifiedPlants" :showProductionType="true" />

    <div>
      <label class="form-label ingredients-approved-label">
        *** New products will not be approved until all its ingredients are
        approved.
      </label>
      <Checkbox
        id="ingredients-approved-checkbox"
        class="ingredients-approved-checkbox"
        v-model="formData.ingredientsAllApproved"
        label="I verify that all ingredients for the new product are currently on our Schedule A as approved, or are being submitted to be added"
        data-cy="ingredients-approved-checkbox"
        required
      />
      <div></div>
    </div>

    <div class="submit-container">
      <Button
        v-if="showBackAction"
        type="button"
        theme-color="secondary"
        :disabled="formSubmitting"
        @click="closeForm(false, -1)"
        data-cy="back-button"
      >
        Return to Cart
      </Button>
      <LoadingButton
        type="submit"
        theme-color="primary"
        :disabled="!anyPlantSelected"
        :loading="formSubmitting"
        data-cy="submit-button"
        >{{ product ? "Update Cart" : "Add Product To Cart" }}</LoadingButton
      >
    </div>
  </form>
</template>

<script setup lang="ts">
import type { IdAndName } from "@/Domain/IdAndName";
import DownloadsPlantGrid from "./DownloadsPlantGrid.vue";
import type { PlantWithSelection } from "@/api/scheduleB";
import { computed, onBeforeMount, ref, watchEffect } from "vue";
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import type { BrandNameAndCompanyIdAndName } from "@/Domain/BrandNameAndCompanyIdAndName";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import {
  Checkbox,
  type RadioButtonChangeEvent,
} from "@progress/kendo-vue-inputs";
import RadioButton from "@/shared/ui/inputs/RadioButton.vue";
import { EnumConfidential } from "@/Domain/EnumConfidential";
import { EnumLabelType } from "@/Domain/EnumLabelType";
import type { UploadFileInfo } from "@progress/kendo-vue-upload";
import { EnumCompanyList } from "@/Domain/EnumCompanyList";
import HelpBlock from "./HelpBlock.vue";
import { useScheduleBStore } from "@/stores/useScheduleBStore";
import type { ProductDisplayInCart } from "@/Domain/ProductDisplayInCart";
import { getConsumerIndustrialString } from "@/shared/helpers/productsHelper";
import ProductAttachmentsView from "./ProductAttachmentsView.vue";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import {
  getAttachmentFilesAsync,
  validateTotalUploadSize,
} from "@/shared/helpers/fileHelper";
import { EnumCompanyStatus } from "@/Domain/EnumCompanyStatus";
import InputField from "@/shared/ui/inputs/InputField.vue";
import FileUpload from "@/shared/ui/inputs/FileUpload.vue";

const main = useMainStore();
const scheduleStore = useScheduleBStore();
const notificationStore = useNotificationStore();

interface DropDownOption {
  text: string;
  value: string;
}

interface ConfidentialOption extends DropDownOption {
  enumValue: EnumConfidential;
}

interface FormData {
  productName: string;
  productNumber: string;
  showLabel: boolean;
  labelName: string;
  labelNumber: string;
  selectedCategory: { Item1: string; Item2: string } | null;
  selectedCI: DropDownOption | null;
  shippedTankersTrailers: boolean;
  canSelectConfidential: boolean;
  selectedConfidential?: DropDownOption | null;
  selectedDpm: DropDownOption | null;
  intermediateMix: boolean;
  passover: boolean;
  selectedBrand: BrandNameAndCompanyIdAndName | null;
  selectedBrandType: string;
  newBrandName: string;
  newBrandUniqueFormula: boolean;
  newBrandCompanyName: string;
  newBrandAddress: string;
  newBrandContactName: string;
  newBrandContactEmail: string;
  newBrandContactPhone: string;
  uploadedDocs: UploadFileInfo[];
  showUpcCode: boolean;
  upcCode: string;
  docsComment: string;
  ingredientsAllApproved: boolean;
}

const props = defineProps<{
  plants: IdAndName[];
  product?: ProductDisplayInCart;
  ownsIds?: number[];
  showBackAction?: boolean;
}>();

const emit = defineEmits<{
  (e: "closeForm", submitted: boolean): void;
  (e: "submittingChanged", value: boolean): void;
}>();

const categories = ref<{ Item1: string; Item2: string }[]>([]);
const brands = ref<BrandNameAndCompanyIdAndName[]>([]);

const ciOptions = [
  { text: "Consumer", value: "C" },
  { text: "Industrial", value: "I" },
  { text: "Consumer & Industrial", value: "CI" },
] as DropDownOption[];

const confidentialOptions = [
  {
    text: "Not Confidential",
    value: "N",
    enumValue: EnumConfidential.NotConfidential,
  },
  {
    text: "Confidential",
    value: "Y",
    enumValue: EnumConfidential.Confidential,
  },
  {
    text: "List only on OU Universal Kosher Database (UKD)",
    value: "U",
    enumValue: EnumConfidential.UkdOnlyButNotPublicUkd,
  },
] as ConfidentialOption[];

const dpmOptions = [
  {
    text: "Pareve",
    value: "Pareve",
  },
  {
    text: "Dairy",
    value: "Dairy",
  },
  {
    text: "Dairy Equipment",
    value: "Dairy Equipment",
  },
  {
    text: "Dairy Fish",
    value: "Dairy Fish",
  },
  {
    text: "Pareve Fish",
    value: "Pareve Fish",
  },
  {
    text: "Glatt",
    value: "Glatt",
  },
  {
    text: "Meat",
    value: "Meat",
  },
] as DropDownOption[];

const formData = ref<FormData>({
  productName: "",
  productNumber: "",
  showLabel: false,
  labelName: "",
  labelNumber: "",
  selectedCategory: null,
  selectedCI: null,
  shippedTankersTrailers: false,
  canSelectConfidential: true,
  selectedConfidential: null,
  selectedDpm: null,
  intermediateMix: false,
  passover: false,
  selectedBrand: null,
  selectedBrandType: "In-House",
  newBrandName: "",
  newBrandUniqueFormula: false,
  newBrandCompanyName: "",
  newBrandAddress: "",
  newBrandContactName: "",
  newBrandContactEmail: "",
  newBrandContactPhone: "",
  showUpcCode: false,
  upcCode: "",
  uploadedDocs: [],
  docsComment: "",
  ingredientsAllApproved: false,
});

const helpBlocksVisibility = ref({
  showProductLabel: false,
  showConfidentiality: false,
  showBrand: false,
  showNewLabel: false,
  showNewIngredients: false,
});

const formSubmitting = ref(false);

const showProductLabelError = ref(false);
const productLabelErrorRef = ref<HTMLElement | null>(null);

const componentName = "ProductApplicationForm.vue";

const newBrandSelected = computed(() => {
  return formData.value.selectedBrand?.Brand == brands.value[0]?.Brand;
});

const selectedBrandType = computed(() => {
  if (newBrandSelected.value) return formData.value.selectedBrandType;
  if (formData.value.selectedBrand?.LabelType === EnumLabelType.InHouse)
    return "In-House";
  if (formData.value.selectedBrand?.LabelType === EnumLabelType.PrivateLabel)
    return "Private Label";
  return "";
});

const certifiedPlants = computed(() => {
  return props.plants.filter(
    (p) => p.Status == EnumCompanyStatus.Certified
  ) as PlantWithSelection[];
});

const anyPlantSelected = computed(() => {
  return certifiedPlants.value.some((p) => p.selected);
});

const productOrLabelSet = computed(() => {
  return (
    formData.value.productName.length > 0 ||
    formData.value.productNumber.length > 0 ||
    formData.value.labelName.length > 0 ||
    formData.value.labelNumber.length > 0
  );
});

const handleBrandTypeChange = (e: RadioButtonChangeEvent) => {
  formData.value.selectedBrandType = e.value;
};

const getCategoriesAsync = async function () {
  return api.get("VueGetCategories", {});
};

const getBrandsAsync = async function () {
  return api.get("VueGetBrands", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    plantList: "",
    subApplication: 3,
  });
};

const getCompanyTbAsync = async function () {
  return api.get("GetCompanyTb", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    subApplication: 3,
    isCalledFromUkd: false,
  });
};

const updateCategoriesBrandsAndConfidentialityAsync = async function () {
  const promises = [
    handleResponsePromiseAsync(
      getCategoriesAsync(),
      (data) => {
        categories.value = data;
      },
      componentName,
      "updateCategoriesBrandsAndConfidentialityAsync"
    ),
    handleResponsePromiseAsync(
      getBrandsAsync(),
      (data) => {
        brands.value = [
          {
            Brand: "Select from Existing Brands",
            BrandSlashDistributorCompanyNamePLCompaniesOnly:
              "Select from Existing Brands",
          } as BrandNameAndCompanyIdAndName,
          ...data,
        ];
        formData.value.selectedBrand = brands.value[0];
      },
      componentName,
      "updateCategoriesBrandsAndConfidentialityAsync"
    ),
    handleResponsePromiseAsync(
      getCompanyTbAsync(),
      (data) => {
        if (data.List == EnumCompanyList.Y) {
          formData.value.canSelectConfidential = false;
          formData.value.selectedConfidential = confidentialOptions.find(
            (c) => c.enumValue == EnumConfidential.NotConfidential
          );
        } else if (data.List == EnumCompanyList.N) {
          formData.value.canSelectConfidential = false;
          formData.value.selectedConfidential = confidentialOptions.find(
            (c) => c.enumValue == EnumConfidential.Confidential
          );
        } else if (data.List == EnumCompanyList.U) {
          formData.value.canSelectConfidential = false;
          formData.value.selectedConfidential = confidentialOptions.find(
            (c) => c.enumValue == EnumConfidential.UkdOnlyButNotPublicUkd
          );
        } else {
          formData.value.canSelectConfidential = true;
        }
      },
      componentName,
      "updateCategoriesBrandsAndConfidentialityAsync"
    ),
  ];
  await Promise.all(promises);
};

const showErrorNotification = function (message: string) {
  notificationStore.notify({
    type: NotificationTypeEnum.error,
    message: message,
    duration: 5000,
  });
};

const fillFormData = function () {
  if (!props.product) return;

  formData.value.productName = props.product.BaseName;
  formData.value.productNumber = props.product.BaseNumber;
  formData.value.labelName = props.product.LabelName;
  formData.value.labelNumber = props.product.LabelNumber;

  formData.value.selectedCategory =
    categories.value.find((c) => c.Item1 == props.product!.Description) || null;

  formData.value.selectedCI =
    ciOptions.find(
      (c) =>
        c.value == getConsumerIndustrialString(props.product!, "C", "I", "")
    ) || null;

  formData.value.shippedTankersTrailers = props.product.ProductShipping == "b";

  formData.value.selectedConfidential = confidentialOptions.find(
    (c) => c.value == props.product!.Confidential
  );

  formData.value.selectedDpm =
    dpmOptions.find((d) => d.value == props.product!.Dpm) || null;

  formData.value.intermediateMix = props.product!._ForInternalUseOnly || false;

  formData.value.passover = props.product!._Pesach;

  const selectedBrand = brands.value.find(
    (b) => b.Brand == props.product!.BrandName
  );
  if (selectedBrand) {
    formData.value.selectedBrand = selectedBrand;
    formData.value.selectedBrandType =
      props.product._LabelType == EnumLabelType.InHouse
        ? "In-House"
        : "Private Label";
  } else {
    formData.value.selectedBrand = brands.value[0];
    formData.value.newBrandName = props.product.BrandName;
    formData.value.newBrandUniqueFormula = props.product.CoPackProductPL;
    formData.value.newBrandCompanyName = props.product.DistributorName;
    formData.value.newBrandAddress = props.product.DistributorAddress;
    formData.value.newBrandContactName = props.product.DistributorContactName;
    formData.value.newBrandContactEmail = props.product.DistributorContactEmail;
    formData.value.newBrandContactPhone = props.product.DistributorContactPhone;
  }

  formData.value.upcCode = props.product.BarCode ?? "";
  formData.value.showUpcCode = formData.value.upcCode.length > 0;

  formData.value.docsComment = props.product.LineItemInstruction;
  formData.value.ingredientsAllApproved = props.product.IngredientsAllApproved;
};

const getProductShipping = function (): string {
  if (
    formData.value.selectedCI?.value == "CI" &&
    formData.value.shippedTankersTrailers
  ) {
    return "bp";
  } else if (
    formData.value.selectedCI?.value == "I" &&
    formData.value.shippedTankersTrailers
  ) {
    return "b";
  } else {
    return "p";
  }
};

const setFormSubmitting = function (value: boolean) {
  formSubmitting.value = value;
  emit("submittingChanged", value);
};

const onSubmit = async function () {
  try {
    if (formSubmitting.value) return;

    setFormSubmitting(true);

    // Validate total files size
    if (!validateTotalUploadSize(formData.value.uploadedDocs)) {
      setFormSubmitting(false);
      return;
    }

    const productShipping = getProductShipping();
    const files = await getAttachmentFilesAsync(formData.value.uploadedDocs);

    if (!productOrLabelSet.value) {
      showProductLabelError.value = true;
      setTimeout(() => {
        productLabelErrorRef.value?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
      setFormSubmitting(false);
      return;
    }

    if (
      newBrandSelected.value &&
      formData.value.selectedBrandType === "Private Label" &&
      files.length == 0
    ) {
      showErrorNotification(
        "Please upload an image of product label file for the new brand"
      );
      setFormSubmitting(false);
      return;
    }

    const selectedPlants = certifiedPlants.value
      .filter((p) => p.selected)
      .map((p) => p.ID);

    let cartId = "";
    if (props.product) cartId = props.product.Id.toString();

    await handleResponsePromiseAsync(
      api.get("VueSaveProductToCart", {
        userLogin: main.userLogin,
        companyLogin: main.companyLogin,
        subApplication: SubApplicationType.Add.toString(),
        id: cartId,
        baseName: formData.value.productName,
        baseNumber: formData.value.productNumber,
        labelName: formData.value.labelName,
        labelNumber: formData.value.labelNumber,
        brandName: newBrandSelected.value
          ? formData.value.newBrandName
          : formData.value.selectedBrand!.Brand,
        distributorId: newBrandSelected.value
          ? "0"
          : formData.value.selectedBrand!.CompanyId.toString(),
        category: formData.value.selectedCategory!.Item1,
        consumerIndustrial: formData.value.selectedCI!.value,
        productShipping: productShipping,
        confidential: formData.value.selectedConfidential!.value,
        forInternalUseOnly: formData.value.intermediateMix.toString(),
        labelType: formData.value.selectedBrandType,
        distributorName: formData.value.newBrandCompanyName,
        distributorAddress: formData.value.newBrandAddress,
        distributorContactName: formData.value.newBrandContactName,
        distributorContactEmail: formData.value.newBrandContactEmail,
        distributorContactPhone: formData.value.newBrandContactPhone,
        lineItemInstruction: formData.value.docsComment,
        dpm: formData.value.selectedDpm!.value,
        passover: formData.value.passover ? "Y" : "",
        owns: selectedPlants.join(";"),
        attachments: formData.value.uploadedDocs.map((d) => d.name).join(";"),
        coPackProductPL: formData.value.newBrandUniqueFormula,
        barCode: formData.value.upcCode,
        ingredientsAllApproved: formData.value.ingredientsAllApproved,
      }),
      (data) => {
        cartId = data;
      },
      componentName,
      "onSubmit",
      () => {
        showErrorNotification(
          "Error while adding products to the cart. Please try again later"
        );
      }
    );

    if (!cartId) {
      setFormSubmitting(false);
      return;
    }

    if (files.length == 0) {
      closeForm(true, selectedPlants.length);
      return;
    }

    await handleResponsePromiseAsync(
      api.post("UploadApplicationFiles", {
        UserLogin: main.userLogin,
        CompanyLogin: main.companyLogin,
        CartId: cartId,
        Files: files,
      }),
      () => {
        closeForm(true, selectedPlants.length);
      },
      componentName,
      "onSubmit",
      () => {
        showErrorNotification(
          "Error while uploading files. Please try again later"
        );
      }
    );
  } catch (e) {
    console.error("ProductApplicationModal, onSubmit()", e);
    showErrorNotification("Error while submitting the form. Please try again");
  }
  setFormSubmitting(false);
};

const closeForm = function (submitted: boolean, cartCount: number = -1) {
  if (cartCount > 0) {
    notificationStore.notify({
      type: NotificationTypeEnum.success,
      message: `Product added to the cart successfully`,
      duration: 5000,
    });
    scheduleStore.addToCartCount(SubApplicationType.Add, cartCount);
  }
  setFormSubmitting(false);
  emit("closeForm", submitted);
};

const clearBrandSelection = function () {
  formData.value.selectedBrand = brands.value[0];
};

watchEffect(() => {
  if (productOrLabelSet.value) {
    showProductLabelError.value = false;
  }
});

onBeforeMount(async () => {
  // Reset plant selection
  certifiedPlants.value.forEach((p) => {
    p.selected = props.ownsIds?.includes(p.ID) || false;
  });

  await updateCategoriesBrandsAndConfidentialityAsync();
  fillFormData();
});
</script>

<style lang="scss">
.application-form {
  .question-icon {
    color: #399cd3;
    width: 5%;
    height: 16px;
    cursor: pointer;
  }
  .clear-brand {
    background: #222;
    color: white;
    border-radius: 50%;
    margin-left: 10px;
    width: 14px;
    height: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    cursor: pointer;
  }

  .ingredients-notification-header {
    color: #055160;
    background-color: #cff4fc;
    border-color: #b6effb;
    position: relative;
    padding: 1rem 1rem;
    margin-bottom: 1rem;
    border: 1px solid transparent;
    border-radius: 0.25rem;
  }

  .form-header {
    background: #efefef;
    padding: 15px;
    font-size: 16px;
    margin-bottom: 20px;
    font-weight: bold;
    border-radius: 4px;

    &.help {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .header-help-container {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 4px;

      .help-link {
        color: #399cd3;
        cursor: pointer;
        text-decoration: none;
        font-size: 12px;

        .question-icon {
          width: 14px;
        }
      }
    }
  }

  .label-link {
    cursor: pointer;
    margin: 24px 0;
    display: block;

    &:hover {
      color: #0a58ca;
    }
  }

  .form-labels {
    margin-bottom: 48px;
  }

  .help-block {
    margin: 20px 0;
  }

  .form-row {
    align-items: baseline;
  }

  .form-control-title {
    display: block;
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 12px;
  }

  .form-control {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    .form-label {
      display: block;
      margin-bottom: 5px;
      width: 25%;
    }

    .k-dropdownlist {
      width: 70%;
    }

    .k-checkbox {
      margin-left: 1rem;
    }

    .form-input {
      width: 70%;
      padding: 0.375rem 0.75rem;
      font-size: 1rem;
      line-height: 1.5;
      color: #495057;
      background-color: #fff;
      background-clip: padding-box;
      border: 1px solid #ced4da;
      border-radius: 0.25rem;
      transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    }

    .form-input-field {
      width: 70%;
    }
  }

  .ingredients-approved-label {
    display: block;
    font-weight: bold;
    margin-bottom: 12px;
  }

  .ingredients-approved-checkbox {
    display: block;
    margin-bottom: 20px;

    & label {
      text-wrap: wrap;
    }
  }

  .form-text-area {
    margin-bottom: 20px;
  }

  .new-brand-container {
    background: #efefef;
    padding: 2rem;
    margin-bottom: 20px;
    border-radius: 8px;

    .k-checkbox-label {
      font-size: 10px;
      font-weight: bold;
    }
  }

  .upload-container {
    margin-bottom: 20px;
  }

  .form-radio-control {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 20px;
  }

  .form-plant-error {
    font-size: 12px;
    font-weight: bold;
    margin-bottom: 20px;
  }

  .plant-grid {
    max-height: 360px;
    margin-bottom: 20px;
  }

  .submit-container {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
  }

  &__validation-error {
    margin-bottom: 20px;
    padding: 8px;
    background-color: #fae2e7;
    border-radius: 0.25rem;
  }
}

.col-sm-3 {
  flex: 0 0 auto;
  width: 25%;
}
.col-sm-8 {
  flex: 0 0 auto;
  width: 66.66666667%;
}
hr {
  border: none;
  margin-bottom: 30px;
  border-bottom: 1px solid #ddd;
}
.or-divider {
  color: #999;
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 10px;
  margin-top: -5px;
}
</style>
